import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { formatDisplayName, type AnonymisationType } from '@/lib/pupils';
import { canCreatePupil, getPlanLimits, planLimitMessage } from '@/lib/entitlements';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, Users, Search, Upload } from 'lucide-react';
import PupilImportDialog from '@/components/PupilImportDialog';
import GradeBadge from '@/components/GradeBadge';
import type { AssessmentGrade } from '@/lib/assessment';

// ---- Types ----------------------------------------------------------------

interface ClassOption {
  id: string;
  name: string;
  yearGroupId: string;
  yearGroupName: string;
}

interface Pupil {
  id: string;
  class_id: string;
  first_name: string;
  last_name: string;
  anonymisation: AnonymisationType;
  is_archived: boolean;
}

type DialogMode = 'add' | 'edit' | null;

const ANONYMISATION_LABELS: Record<AnonymisationType, string> = {
  full_name: 'Full name',
  first_initial: 'First initial',
  initials_only: 'Initials only',
};

// ---- Component ------------------------------------------------------------

export default function Pupils() {
  const { user, profile, hasRole, schoolPlan, planTier } = useAuth();
  const { toast } = useToast();
  const isSchoolWideViewer = hasRole('school_admin') || hasRole('slt');
  const canManagePupils = hasRole('school_admin');
  // Individual plan users are school_admin but should use the teacher_classes path
  // (the 3-level nested admin query fails to return results for them)
  const useAdminPath = isSchoolWideViewer && !!profile?.school_id && schoolPlan === 'school';

  // Data
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [pupils, setPupils] = useState<Pupil[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);


  // Filters
  const [search, setSearch] = useState('');
  const [filterClassId, setFilterClassId] = useState<string>('all');
  const [filterYearGroupId, setFilterYearGroupId] = useState<string>('all');

  // Overall grades (pupil_id → current_grade)
  const [overallGrades, setOverallGrades] = useState<Map<string, AssessmentGrade>>(new Map());

  // CSV import
  const [importOpen, setImportOpen] = useState(false);

  // Dialog
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingPupil, setEditingPupil] = useState<Pupil | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [classId, setClassId] = useState('');
  const [anonymisation, setAnonymisation] = useState<AnonymisationType>('full_name');

  // ---- Data fetching -------------------------------------------------------

  const fetchOverallGrades = async (pupilIds: string[]) => {
    if (pupilIds.length === 0) return;
    const { data } = await supabase
      .from('pupil_overall_grades')
      .select('pupil_id, current_grade')
      .in('pupil_id', pupilIds);
    if (data) {
      setOverallGrades(new Map(
        data
          .filter((g) => g.current_grade != null)
          .map((g) => [g.pupil_id, g.current_grade as AssessmentGrade]),
      ));
    }
  };

  const fetchData = useCallback(async () => {
    if (!user || !profile?.school_id) return;
    setLoading(true);
    setHasError(false);

    // ── Admin path (school plan): query classes directly filtered by school ──
    if (useAdminPath) {
      const { data: classData, error: classError } = await supabase
        .from('classes')
        .select('id, name, year_groups!inner(id, name, key_stages!inner(school_id))')
        .eq('year_groups.key_stages.school_id', profile!.school_id);

      if (classError) {
        toast({ title: 'Failed to load classes', description: classError.message, variant: 'destructive' });
        setHasError(true);
        setLoading(false);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sorted: ClassOption[] = ((classData ?? []) as any[])
        .map((row) => ({
          id: row.id,
          name: row.name,
          yearGroupId: row.year_groups?.id ?? '',
          yearGroupName: row.year_groups?.name ?? '',
        }))
        .sort((a: ClassOption, b: ClassOption) => a.name.localeCompare(b.name));

      setClasses(sorted);
      setClassId((prev) => prev || sorted[0]?.id || '');

      const { data: pupilData, error: pupilError } = await supabase
        .from('pupils')
        .select('*')
        .eq('is_archived', false);

      if (pupilError) {
        toast({ title: 'Failed to load pupils', description: pupilError.message, variant: 'destructive' });
        setHasError(true);
        setLoading(false);
        return;
      }

      const accessibleClassIds = new Set(sorted.map((c) => c.id));
      const loadedPupils = ((pupilData ?? []) as Pupil[]).filter((p) => accessibleClassIds.has(p.class_id));
      setPupils(loadedPupils);
      await fetchOverallGrades(loadedPupils.map((p) => p.id));
      setLoading(false);
      return;
    }

    // ── Teacher / individual path: two-query pattern (avoids RLS lateral join resolution issues) ──
    const { data: tcData, error: tcError } = await supabase
      .from('teacher_classes')
      .select('class_id')
      .eq('user_id', user.id);

    if (tcError) {
      toast({ title: 'Failed to load classes', description: tcError.message, variant: 'destructive' });
      setHasError(true);
      setLoading(false);
      return;
    }

    const tcClassIds = (tcData ?? []).map((tc) => tc.class_id);
    if (tcClassIds.length === 0) {
      setClasses([]);
      setPupils([]);
      setLoading(false);
      return;
    }

    const { data: classData, error: classError } = await supabase
      .from('classes')
      .select('id, name, year_groups(id, name)')
      .in('id', tcClassIds);

    if (classError) {
      toast({ title: 'Failed to load classes', description: classError.message, variant: 'destructive' });
      setHasError(true);
      setLoading(false);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sorted: ClassOption[] = ((classData ?? []) as any[])
      .map((row) => ({
        id: row.id,
        name: row.name,
        yearGroupId: row.year_groups?.id ?? '',
        yearGroupName: row.year_groups?.name ?? '',
      }))
      .sort((a: ClassOption, b: ClassOption) => a.name.localeCompare(b.name));

    if (sorted.length === 0) {
      setClasses([]);
      setPupils([]);
      setLoading(false);
      return;
    }

    setClasses(sorted);
    setClassId((prev) => prev || sorted[0]?.id || '');

    const classIds = sorted.map((c) => c.id);
    const { data: pupilData, error: pupilError } = await supabase
      .from('pupils')
      .select('*')
      .eq('is_archived', false)
      .in('class_id', classIds);

    if (pupilError) {
      toast({ title: 'Failed to load pupils', description: pupilError.message, variant: 'destructive' });
      setHasError(true);
      setLoading(false);
      return;
    }

    const loadedPupils = (pupilData ?? []) as Pupil[];
    setPupils(loadedPupils);
    await fetchOverallGrades(loadedPupils.map((p) => p.id));
    setLoading(false);
  }, [user, profile, useAdminPath, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ---- Derived data --------------------------------------------------------

  const yearGroups = useMemo(() => {
    const seen = new Set<string>();
    return classes.filter((c) => {
      if (!c.yearGroupId || seen.has(c.yearGroupId)) return false;
      seen.add(c.yearGroupId);
      return true;
    });
  }, [classes]);

  const filteredPupils = useMemo(() => {
    return pupils.filter((p) => {
      if (filterClassId !== 'all' && p.class_id !== filterClassId) return false;
      if (filterYearGroupId !== 'all') {
        const cls = classes.find((c) => c.id === p.class_id);
        if (!cls || cls.yearGroupId !== filterYearGroupId) return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const full = `${p.first_name} ${p.last_name}`.toLowerCase();
        if (!full.includes(q)) return false;
      }
      return true;
    });
  }, [pupils, filterClassId, filterYearGroupId, classes, search]);

  // Group filtered pupils by class
  const groupedByClass = useMemo(() => {
    const map = new Map<string, Pupil[]>();
    for (const cls of classes) {
      const inClass = filteredPupils.filter((p) => p.class_id === cls.id);
      if (inClass.length > 0) map.set(cls.id, inClass);
    }
    return map;
  }, [classes, filteredPupils]);

  // ---- Dialog helpers ------------------------------------------------------

  const openAdd = () => {
    setEditingPupil(null);
    setFirstName('');
    setLastName('');
    setClassId(classes[0]?.id ?? '');
    setAnonymisation('full_name');
    setDialogMode('add');
  };

  const openEdit = (pupil: Pupil) => {
    setEditingPupil(pupil);
    setFirstName(pupil.first_name);
    setLastName(pupil.last_name);
    setClassId(pupil.class_id);
    setAnonymisation(pupil.anonymisation);
    setDialogMode('edit');
  };

  const closeDialog = () => {
    setDialogMode(null);
    setEditingPupil(null);
  };

  // ---- Mutations -----------------------------------------------------------

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast({ title: 'First name and last name are required.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (dialogMode === 'add') {
        // Enforce plan pupil limit before inserting.
        const limits = getPlanLimits(planTier);
        if (limits.maxPupils !== -1) {
          const activePupilCount = pupils.filter((p) => !p.is_archived).length;
          if (!canCreatePupil(planTier, activePupilCount)) {
            toast({
              title: 'Pupil limit reached',
              description: planLimitMessage('pupil', limits.maxPupils, planTier),
              variant: 'destructive',
            });
            setSaving(false);
            return;
          }
        }

        const { error } = await supabase.from('pupils').insert({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          class_id: classId,
          anonymisation,
        });
        if (error) {
          toast({ title: 'Failed to add pupil', description: error.message, variant: 'destructive' });
        } else {
          await fetchData();
          setFirstName('');
          setLastName('');
          setClassId(classes[0]?.id ?? '');
          setAnonymisation('full_name');
          toast({ title: 'Pupil added.' });
        }
      } else if (dialogMode === 'edit' && editingPupil) {
        const { error } = await supabase
          .from('pupils')
          .update({
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            class_id: classId,
            anonymisation,
          })
          .eq('id', editingPupil.id);
        if (error) {
          toast({ title: 'Failed to update pupil', description: error.message, variant: 'destructive' });
        } else {
          await fetchData();
          closeDialog();
          toast({ title: 'Pupil updated.' });
        }
      }
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (pupil: Pupil) => {
    const displayName = formatDisplayName(pupil.first_name, pupil.last_name, pupil.anonymisation);
    const confirmed = window.confirm(
      `Archive ${displayName}? They will no longer appear in your class lists.`,
    );
    if (!confirmed) return;

    const { error } = await supabase
      .from('pupils')
      .update({ is_archived: true })
      .eq('id', pupil.id);

    if (error) {
      toast({ title: 'Failed to archive pupil', description: error.message, variant: 'destructive' });
    } else {
      await fetchData();
      toast({ title: `${displayName} archived.` });
    }
  };

  // ---- Render --------------------------------------------------------------

  const dialogTitle = dialogMode === 'add'
    ? 'Add Pupil'
    : editingPupil
      ? `Edit ${formatDisplayName(editingPupil.first_name, editingPupil.last_name, editingPupil.anonymisation)}`
      : 'Edit Pupil';

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-foreground">Manage Pupils</h1>
          <div className="flex gap-2">
            {canManagePupils && (
              <Button
                variant="outline"
                onClick={() => setImportOpen(true)}
                disabled={loading}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </Button>
            )}
            {canManagePupils && (
              <Button onClick={openAdd} disabled={loading || classes.length === 0}>
                Add Pupil
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        {!loading && !hasError && classes.length > 0 && (
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-48">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterYearGroupId} onValueChange={(v) => { setFilterYearGroupId(v); setFilterClassId('all'); }}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All year groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All year groups</SelectItem>
                {yearGroups.map((yg) => (
                  <SelectItem key={yg.yearGroupId} value={yg.yearGroupId}>
                    {yg.yearGroupName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterClassId} onValueChange={setFilterClassId}>
              <SelectTrigger className="w-44">
                <SelectValue placeholder="All classes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All classes</SelectItem>
                {classes
                  .filter((c) => filterYearGroupId === 'all' || c.yearGroupId === filterYearGroupId)
                  .map((cls) => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error */}
        {!loading && hasError && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Failed to load pupils. Please refresh.
            </CardContent>
          </Card>
        )}

        {/* No classes */}
        {!loading && !hasError && classes.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
              {schoolPlan === 'school' ? (
                <p className="font-medium">No classes assigned. Contact your school administrator.</p>
              ) : (
                <p className="font-medium">
                  No classes found.{' '}
                  <Link to="/my-classes" className="text-primary underline underline-offset-2">
                    Create a class in My Classes
                  </Link>{' '}
                  to get started.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* No search results */}
        {!loading && !hasError && classes.length > 0 && filteredPupils.length === 0 && pupils.length > 0 && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              No pupils match your search or filter.
            </CardContent>
          </Card>
        )}

        {/* Tables grouped by class */}
        {!loading && !hasError && Array.from(groupedByClass.entries()).map(([clsId, classPupils]) => {
          const cls = classes.find((c) => c.id === clsId);
          if (!cls) return null;
          return (
            <Card key={clsId} className="shadow-soft">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">
                  {cls.name}
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    {cls.yearGroupName}
                  </span>
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    · {classPupils.length} {classPupils.length === 1 ? 'pupil' : 'pupils'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <Table className="table-fixed w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-full min-w-[10rem]">Name</TableHead>
                      <TableHead className="w-32 whitespace-nowrap">Current Grade</TableHead>
                      <TableHead className="w-40 whitespace-nowrap">Display setting</TableHead>
                      <TableHead className="w-36 text-right whitespace-nowrap">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classPupils
                      .sort((a, b) => a.last_name.localeCompare(b.last_name))
                      .map((pupil) => (
                        <TableRow key={pupil.id}>
                          <TableCell className="font-medium truncate max-w-0">
                            {formatDisplayName(pupil.first_name, pupil.last_name, pupil.anonymisation)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {overallGrades.has(pupil.id) ? (
                              <GradeBadge grade={overallGrades.get(pupil.id)!} size="sm" />
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            <Badge variant="outline" className="text-xs">
                              {ANONYMISATION_LABELS[pupil.anonymisation]}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="inline-flex gap-2">
                              {canManagePupils && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEdit(pupil)}
                                >
                                  Edit
                                </Button>
                              )}
                              {canManagePupils && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => handleArchive(pupil)}
                                >
                                  Archive
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })}

        {/* CSV Import Dialog */}
        {canManagePupils && (
          <PupilImportDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            onComplete={fetchData}
          />
        )}

        {/* Add / Edit Dialog */}
        <Dialog
          open={canManagePupils && dialogMode !== null}
          onOpenChange={(open) => { if (!open) closeDialog(); }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{dialogTitle}</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  disabled={saving}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  disabled={saving}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="classSelect">Class</Label>
                <Select
                  value={classId}
                  onValueChange={setClassId}
                  disabled={saving}
                >
                  <SelectTrigger id="classSelect">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((cls) => (
                      <SelectItem key={cls.id} value={cls.id}>
                        {cls.name} ({cls.yearGroupName})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="anonymisationSelect">Display name setting</Label>
                <Select
                  value={anonymisation}
                  onValueChange={(v) => setAnonymisation(v as AnonymisationType)}
                  disabled={saving}
                >
                  <SelectTrigger id="anonymisationSelect">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_name">Full name</SelectItem>
                    <SelectItem value="first_initial">First initial + last name</SelectItem>
                    <SelectItem value="initials_only">Initials only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={closeDialog} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
